import { ref, readonly } from 'vue'
import { CommunityService } from '@/services/communityService'

// Shared state across all component instances
const communityService = new CommunityService()

const posts = ref([])
const allPosts = ref([])
const videos = ref([])
const achievements = ref({
  points: 0,
  level: 1,
  badges: []
})

const isLoading = ref(false)
const isLoaded = ref(false)
const loadError = ref(null)

// Preload function - can be called from anywhere
async function preloadCommunityData() {
  // Only load once
  if (isLoaded.value || isLoading.value) {
    return
  }

  isLoading.value = true
  loadError.value = null

  try {
    console.log('[Community Preload] Starting background data load...')

    // Load all data in parallel
    const [postsResult, allPostsResult, videosResult] = await Promise.all([
      communityService.getPosts(null, 10),
      communityService.getPosts(null),
      communityService.getFeaturedVideos()
    ])

    // Store posts
    if (postsResult.success && postsResult.posts) {
      posts.value = postsResult.posts
      console.log(`[Community Preload] Loaded ${posts.value.length} posts`)
    }

    // Store all posts for counting
    if (allPostsResult.success && allPostsResult.posts) {
      allPosts.value = allPostsResult.posts
      console.log(`[Community Preload] Loaded ${allPosts.value.length} total posts for counting`)
    }

    // Store videos
    if (videosResult.success && videosResult.videos) {
      videos.value = videosResult.videos
      console.log(`[Community Preload] Loaded ${videos.value.length} videos`)
    }

    isLoaded.value = true
    console.log('[Community Preload] ✓ All data loaded successfully')
  } catch (error) {
    console.error('[Community Preload] Error loading data:', error)
    loadError.value = error
  } finally {
    isLoading.value = false
  }
}

// Refresh data - can be called to reload
async function refreshCommunityData() {
  isLoaded.value = false
  await preloadCommunityData()
}

// Clear cached data
function clearCommunityData() {
  posts.value = []
  allPosts.value = []
  videos.value = []
  achievements.value = {
    points: 0,
    level: 1,
    badges: []
  }
  isLoaded.value = false
  loadError.value = null
}

export function useCommunityPreload() {
  return {
    // State (readonly to prevent external modification)
    posts: readonly(posts),
    allPosts: readonly(allPosts),
    videos: readonly(videos),
    achievements: readonly(achievements),
    isLoading: readonly(isLoading),
    isLoaded: readonly(isLoaded),
    loadError: readonly(loadError),

    // Methods
    preloadCommunityData,
    refreshCommunityData,
    clearCommunityData
  }
}
